/**
 * Rollups over computed shift rows.
 *
 * Kept separate from payrollService so the CSV layer can group rows without
 * importing the service (which would create a cycle).
 */

import { roundMoney } from './time';
import type {
  PayrollAggregateRow,
  PayrollExportRow,
  PayrollExportSummary,
  PayrollGroupBy,
  PayrollTotals,
} from './types';

export const MIXED_PAY_BAND_CODE = 'MIXED';
export const MIXED_PAY_BAND_NAME = 'Mixed rates';

export function emptySummaryTotals() {
  return {
    shiftCount: 0,
    completeShiftCount: 0,
    siteCount: 0,
    grossHours: 0,
    unpaidBreakHours: 0,
    roundingAdjustmentHours: 0,
    payableHours: 0,
    ordinaryHours: 0,
    overtimeHours: 0,
    doubleTimeHours: 0,
    minimumShiftTopUpHours: 0,
    sundayHours: 0,
    publicHolidayHours: 0,
    unsocialHours: 0,
    unsocialShiftCount: 0,
    ordinaryPay: 0,
    overtimePay: 0,
    doubleTimePay: 0,
    sundayPremiumPay: 0,
    publicHolidayPremiumPay: 0,
    unsocialPremiumPay: 0,
    estimatedBasePay: 0,
    estimatedTotalPay: 0,
    leaveAccruedHours: 0,
    needsReviewCount: 0,
  };
}

export type SummaryTotals = ReturnType<typeof emptySummaryTotals>;

const HOUR_FIELDS = [
  'grossHours', 'unpaidBreakHours', 'roundingAdjustmentHours', 'payableHours', 'ordinaryHours',
  'overtimeHours', 'doubleTimeHours', 'minimumShiftTopUpHours', 'sundayHours', 'publicHolidayHours',
  'unsocialHours', 'premiumPay', 'totalPay',
] as const;

const MONEY_FIELDS = [
  'ordinaryPay', 'overtimePay', 'doubleTimePay', 'sundayPremiumPay', 'publicHolidayPremiumPay',
  'unsocialPremiumPay', 'estimatedBasePay', 'estimatedTotalPay', 'premiumPay', 'totalPay',
] as const;

/** Accumulating floats drifts; re-round each aggregate to cent precision. */
export function normaliseAggregate<T extends Record<string, unknown>>(summary: T): T {
  const next = { ...summary } as Record<string, unknown>;

  HOUR_FIELDS.forEach((field) => {
    if (typeof next[field] === 'number' && !MONEY_FIELDS.includes(field as never)) {
      next[field] = Math.round((next[field] as number) * 100) / 100;
    }
  });
  MONEY_FIELDS.forEach((field) => {
    if (typeof next[field] === 'number') {
      next[field] = roundMoney(next[field] as number);
    }
  });
  if (typeof next.leaveAccruedHours === 'number') {
    next.leaveAccruedHours = Math.round((next.leaveAccruedHours as number) * 1000) / 1000;
  }

  return next as T;
}

export function addRow(target: SummaryTotals, row: PayrollExportRow): void {
  target.shiftCount += 1;
  target.completeShiftCount += (
    row.status === 'completed'
    && Boolean(row.sourceStartAt)
    && Boolean(row.sourceEndAt)
    && !row.flags.includes('Invalid clock interval')
  ) ? 1 : 0;
  target.grossHours += row.grossHours;
  target.unpaidBreakHours += row.unpaidBreakHours;
  target.roundingAdjustmentHours += row.roundingAdjustmentHours;
  target.payableHours += row.payableHours;
  target.ordinaryHours += row.ordinaryHours;
  target.overtimeHours += row.overtimeHours;
  target.doubleTimeHours += row.doubleTimeHours;
  target.minimumShiftTopUpHours += row.minimumShiftTopUpHours;
  target.sundayHours += row.sundayHours;
  target.publicHolidayHours += row.publicHolidayHours;
  target.unsocialHours += row.unsocialHours;
  target.unsocialShiftCount += row.unsocialPremiumQualified ? 1 : 0;
  target.ordinaryPay += row.ordinaryPay;
  target.overtimePay += row.overtimePay;
  target.doubleTimePay += row.doubleTimePay;
  target.sundayPremiumPay += row.sundayPremiumPay;
  target.publicHolidayPremiumPay += row.publicHolidayPremiumPay;
  target.unsocialPremiumPay += row.unsocialPremiumPay;
  target.estimatedBasePay += row.estimatedBasePay;
  target.estimatedTotalPay += row.estimatedTotalPay;
  target.leaveAccruedHours += row.leaveAccruedHours;
  target.needsReviewCount += row.flags.length ? 1 : 0;
}

export function buildSummaries(rows: PayrollExportRow[]): PayrollExportSummary[] {
  const summaries = new Map<string, PayrollExportSummary>();
  const sitesByEmployee = new Map<string, Set<string>>();

  rows.forEach((row) => {
    if (!summaries.has(row.employeeId)) {
      summaries.set(row.employeeId, {
        employeeId: row.employeeId,
        employeeName: row.employeeName,
        payrollReference: row.payrollReference,
        payBandId: row.payBandId,
        payBandCode: row.payBandCode,
        payBandName: row.payBandName,
        costCentre: row.costCentre,
        employmentType: row.employmentType,
        hourlyRate: row.hourlyRate,
        ...emptySummaryTotals(),
      });
      sitesByEmployee.set(row.employeeId, new Set());
    }

    const summary = summaries.get(row.employeeId)!;
    const rateContextChanged = summary.payBandId !== row.payBandId
      || summary.payBandCode !== row.payBandCode
      || summary.payBandName !== row.payBandName
      || summary.hourlyRate !== row.hourlyRate;

    if (rateContextChanged) {
      summary.payBandId = null;
      summary.payBandCode = MIXED_PAY_BAND_CODE;
      summary.payBandName = MIXED_PAY_BAND_NAME;
      summary.hourlyRate = null;
    }
    if (summary.payrollReference !== row.payrollReference) summary.payrollReference = null;
    if (summary.costCentre !== row.costCentre) summary.costCentre = null;
    if (summary.employmentType !== row.employmentType) summary.employmentType = null;

    addRow(summary, row);
    if (row.siteId) sitesByEmployee.get(row.employeeId)!.add(row.siteId);
  });

  return [...summaries.values()]
    .map((summary) => normaliseAggregate({
      ...summary,
      siteCount: sitesByEmployee.get(summary.employeeId)?.size ?? 0,
    }))
    .sort((left, right) => left.employeeName.localeCompare(right.employeeName));
}

export function buildTotals(rows: PayrollExportRow[], employeeCount: number): PayrollTotals {
  const totals = emptySummaryTotals();
  rows.forEach((row) => addRow(totals, row));

  return normaliseAggregate({
    ...totals,
    employeeCount,
    siteCount: new Set(rows.map((row) => row.siteId).filter(Boolean)).size,
  });
}

export function aggregateRows(rows: PayrollExportRow[], groupBy: PayrollGroupBy): PayrollAggregateRow[] {
  const groups = new Map<string, {
    groupKey: string;
    groupLabel: string;
    employees: Set<string>;
    totals: SummaryTotals;
  }>();

  const keyFor = (row: PayrollExportRow): [string, string] => {
    switch (groupBy) {
      case 'site': return [row.siteId ?? 'unassigned', row.siteName];
      case 'pay_band': return [row.payBandCode, row.payBandName];
      case 'cost_centre': return [row.costCentre ?? 'unassigned', row.costCentre ?? 'Unassigned'];
      case 'week': return [row.weekStartDate, `Week of ${row.weekStartDate}`];
      case 'employment_type': return [row.employmentType ?? 'unknown', row.employmentType ?? 'Unknown'];
      default: return [row.employeeId, row.employeeName];
    }
  };

  rows.forEach((row) => {
    const [groupKey, groupLabel] = keyFor(row);
    if (!groups.has(groupKey)) {
      groups.set(groupKey, { groupKey, groupLabel, employees: new Set(), totals: emptySummaryTotals() });
    }
    const group = groups.get(groupKey)!;
    group.employees.add(row.employeeId);
    addRow(group.totals, row);
  });

  return [...groups.values()]
    .map((group) => normaliseAggregate({
      groupKey: group.groupKey,
      groupLabel: group.groupLabel,
      employeeCount: group.employees.size,
      shiftCount: group.totals.shiftCount,
      payableHours: group.totals.payableHours,
      ordinaryHours: group.totals.ordinaryHours,
      overtimeHours: group.totals.overtimeHours,
      doubleTimeHours: group.totals.doubleTimeHours,
      unsocialHours: group.totals.unsocialHours,
      sundayHours: group.totals.sundayHours,
      publicHolidayHours: group.totals.publicHolidayHours,
      ordinaryPay: group.totals.ordinaryPay,
      overtimePay: group.totals.overtimePay,
      premiumPay: roundMoney(
        group.totals.sundayPremiumPay
        + group.totals.publicHolidayPremiumPay
        + group.totals.unsocialPremiumPay,
      ),
      totalPay: group.totals.estimatedTotalPay,
    }))
    .sort((left, right) => right.totalPay - left.totalPay);
}
